import React, { useState, useCallback } from 'react';

import { useAuth } from '../../context/AuthContext';
import useAuthGuard from '../../hooks/useAuthGuard';

import { LoginModal } from './LoginModal';
import styles from './SaveButton.module.css';

/**
 * 统一的Save按钮组件
 * 支持收藏/取消收藏状态切换，包含乐观更新和无障碍支持
 *
 * @param {Object} props
 * @param {boolean} props.isFavorited - 是否已收藏
 * @param {string} props.itemType - 项目类型 ('work' | 'collaboration')
 * @param {string} props.itemId - 项目ID
 * @param {Function} props.onToggle - 切换收藏状态的回调函数
 * @param {string} props.className - 额外的CSS类名
 * @param {boolean} props.disabled - 是否禁用
 * @param {string} props.size - 按钮大小 ('sm' | 'md' | 'lg')
 */
export function SaveButton(props) {
  const {
    isFavorited = false,
    itemType,
    itemId,
    onToggle,
    className = '',
    disabled = false,
    size = 'md',
  } = props;

  const { user: currentUser } = useAuth();
  // 2026-04-17 登录守卫：未登录点击改为弹 LoginModal，不再禁用按钮
  const {
    requireLogin,
    isLoginModalOpen,
    handleLoginSuccess,
    handleClose: handleLoginModalClose,
  } = useAuthGuard();
  const [isOptimistic, setIsOptimistic] = useState(false);
  const [optimisticState, setOptimisticState] = useState(null);

  // 檢查認證狀態
  const isAuthenticated = !!currentUser;
  // 禁用条件不再含"未登录"——未登录走弹窗流程（由 requireLogin 处理）
  const isDisabled = disabled;

  // 当前显示的状态（乐观更新优先）
  const currentState = isOptimistic ? optimisticState : isFavorited;

  // 处理点击事件
  const handleClick = useCallback(async () => {
    if (isDisabled) return;

    // 2026-04-17 登录守卫：未登录时弹登录窗并中止收藏流程
    if (!requireLogin()) return;

    // 乐观更新：立即切换状态
    setIsOptimistic(true);
    setOptimisticState(!currentState);

    try {
      await onToggle(itemType, itemId, !currentState);
      setIsOptimistic(false);
      setOptimisticState(null);
    } catch (error) {
      setIsOptimistic(false);
      setOptimisticState(null);
      console.error('[Favorites] Save toggle failed:', error);
    }
  }, [isDisabled, currentState, onToggle, itemType, itemId, requireLogin]);

  // 处理键盘事件
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  // 按钮样式类
  const buttonClass = `
    ${styles.saveButton}
    ${styles[size]}
    ${
      currentState
        ? itemType === 'work'
          ? styles.favoritedWork
          : styles.favoritedCollab
        : styles.unfavorited
    }
    ${isDisabled ? styles.disabled : ''}
    ${className}
  `.trim();

  // 图标样式类
  const iconClass = `
    ${styles.icon}
    ${currentState ? styles.filled : styles.outlined}
  `.trim();

  // 生成 tooltip 文本
  const getTooltipText = () => {
    if (!isAuthenticated) {
      return 'Please log in to save favorites';
    }
    return currentState ? 'Remove from favorites' : 'Add to favorites';
  };

  return (
    <>
      <button
        type='button'
        className={buttonClass}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        aria-pressed={currentState}
        aria-label={getTooltipText()}
        title={getTooltipText()}
      >
        {/* 收藏图标 */}
        <svg
          className={iconClass}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
          aria-hidden='true'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z'
          />
        </svg>

        {/* 按钮文案 */}
        <span className={styles.text}>{currentState ? 'Saved' : 'Save'}</span>
      </button>

      {/* 2026-04-17 登录守卫弹窗（未登录点击收藏时触发） */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={handleLoginModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
}
